import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  Commission,
  EarningsStats,
  EarningsSummary,
  getEarningsStats,
  getMyCommissions,
  getMyEarnings,
} from '../../api/affiliate/affiliate.api';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const RANGES = [
  { days: 7, label: '7 ngày' },
  { days: 14, label: '14 ngày' },
  { days: 30, label: '30 ngày' },
];

const ca = (n?: number) => `${Number(n || 0).toLocaleString('vi-VN')} Ca`;

const STATUS_VI: Record<string, { label: string; cls: string }> = {
  Pending: { label: 'Chờ (đổi trả)', cls: 'bg-amber-100 text-amber-700' },
  Paid: { label: 'Đã cộng Ca', cls: 'bg-emerald-100 text-emerald-700' },
  Rejected: { label: 'Bị huỷ', cls: 'bg-rose-100 text-rose-700' },
};

const Metric: React.FC<{ label: string; value: string; sub?: string }> = ({ label, value, sub }) => (
  <div className="rounded-lg ui-divider border p-3 flex-1 min-w-[140px]">
    <div className="text-xs ui-muted">{label}</div>
    <div className="text-lg font-bold ui-text">{value}</div>
    {sub ? <div className="text-[11px] ui-muted">{sub}</div> : null}
  </div>
);

const AffiliateDashboard: React.FC = () => {
  const [days, setDays] = useState(14);
  const [summary, setSummary] = useState<EarningsSummary | null>(null);
  const [stats, setStats] = useState<EarningsStats | null>(null);
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMyEarnings().then(setSummary).catch(() => undefined);
    getMyCommissions().then(setCommissions).catch(() => undefined);
  }, []);

  useEffect(() => {
    setLoading(true);
    getEarningsStats(days)
      .then(setStats)
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [days]);

  const chartData = {
    labels: (stats?.daily || []).map((d) => d.date.slice(5)),
    datasets: [
      {
        label: 'Hoa hồng (Ca)',
        data: (stats?.daily || []).map((d) => d.commissionCa),
        backgroundColor: '#0071A6',
        borderRadius: 4,
      },
    ],
  };

  return (
    <div className="space-y-4">
      {/* Tổng quan */}
      <div className="flex flex-wrap gap-2">
        <Metric label="Hoa hồng đang chờ" value={ca(summary?.pendingCa)} sub={`${summary?.pendingCount ?? 0} đơn đang chờ`} />
        <Metric label="Đã nhận (rút được)" value={ca(summary?.paidCa)} />
        <Metric
          label="Mở khoá gần nhất"
          value={summary?.nextAvailableAt ? new Date(summary.nextAvailableAt).toLocaleDateString('vi-VN') : '—'}
          sub="Đơn chờ sớm nhất vào Ca"
        />
        <Metric label={`Tổng ${days} ngày`} value={ca(stats?.totalCommissionCa)} sub={`${stats?.totalOrders ?? 0} đơn`} />
        <Metric label={`Lượt click (${days} ngày)`} value={(stats?.totalClicks ?? 0).toLocaleString('vi-VN')} />
        <Metric label="Tỉ lệ chuyển đổi" value={`${stats?.conversionRate ?? 0}%`} sub="đơn / click" />
      </div>

      {/* Biểu đồ hoa hồng theo ngày */}
      <div className="rounded-xl ui-divider border p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold ui-text">Hoa hồng theo ngày</h3>
          <div className="flex gap-1">
            {RANGES.map((r) => (
              <button
                key={r.days}
                onClick={() => setDays(r.days)}
                className={`text-xs rounded-full px-3 py-1 border ${
                  days === r.days ? 'bg-[#0071A6] text-white border-[#0071A6]' : 'ui-muted ui-divider'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
        {loading ? (
          <div className="h-40 grid place-items-center"><Loader2 className="animate-spin ui-muted" /></div>
        ) : (
          <div className="h-56">
            <Bar data={chartData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }} />
          </div>
        )}
      </div>

      {/* Danh sách đơn hoa hồng của tôi */}
      <div className="rounded-xl ui-divider border p-4">
        <h3 className="font-semibold ui-text mb-2">Đơn hoa hồng của tôi</h3>
        {commissions.length === 0 ? (
          <p className="ui-muted text-sm">Chưa có đơn nào. Gửi link sản phẩm cho mọi người để bắt đầu có hoa hồng.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="ui-muted text-left">
                <tr>
                  <th className="py-2 pr-3">Mã đơn</th>
                  <th className="py-2 pr-3">Mạng</th>
                  <th className="py-2 pr-3">Hoa hồng</th>
                  <th className="py-2 pr-3">Trạng thái</th>
                  <th className="py-2 pr-3">Ngày</th>
                  <th className="py-2">Mở khoá</th>
                </tr>
              </thead>
              <tbody>
                {commissions.map((c) => {
                  const st = STATUS_VI[c.status] || { label: c.status, cls: 'bg-slate-200 text-slate-700' };
                  return (
                    <tr key={c.id} className="border-t ui-divider ui-text">
                      <td className="py-2 pr-3 font-mono text-xs">{c.orderRef || c.id.slice(0, 8)}</td>
                      <td className="py-2 pr-3">{c.network}</td>
                      <td className="py-2 pr-3 font-semibold">{ca(c.ca)}</td>
                      <td className="py-2 pr-3">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                        {c.status === 'Rejected' && c.rejectReason ? <span className="ml-1 text-[11px] ui-muted">({c.rejectReason})</span> : null}
                      </td>
                      <td className="py-2 pr-3 text-xs ui-muted">{new Date(c.createdAt).toLocaleDateString('vi-VN')}</td>
                      <td className="py-2 text-xs ui-muted">
                        {c.status === 'Pending' ? new Date(c.availableAt).toLocaleDateString('vi-VN') : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AffiliateDashboard;
